<template>
  <div
    class="w-full h-full flex flex-col pt-4 px-3 pb-0! tablet-h:p-[14px] tablet-h:pr-[64px] desktop:pr-[14px] relative"
    :style="componentStyles"
    @click="handleClickOutside"
    @dragover.prevent="handleDragOver"
    @dragleave="handleDragLeave"
    @drop.prevent="handleDrop"
  >
    <UpsellBanner
      v-if="billingStore.billing?.upsell"
      class="px-2 py-[7.5px] hidden tablet-h:flex"
    />

    <div
      class="absolute w-full h-full top-0 right-0 left-0 bottom-0 z-10 pointer-events-none"
      :class="overlayClasses"
    />

    <!-- Header -->
    <div class="tablet-h:ml-10 flex items-end justify-between mb-5">
      <div class="w-full">
        <Breadcrumb
          v-if="showBreadcrumb"
          class="pb-3 text-[18px] leading-[20px] tracking-[-0.25px]"
          :path="path"
          :iconSize="22"
          @location="handleLocationUpdate"
        />
        <div class="flex">
          <UiButton
            class="mr-3"
            primary
            icon="Upload"
            :disabled="!canUpload"
            @click="handleUploadClick"
          >
            Upload
          </UiButton>
          <UiButton icon="Plus" class="mr-3" @click="handleNewFolderClick">
            <div class="min-[390px]:hidden font-secondary">Folder</div>
            <div class="hidden min-[390px]:block font-secondary">
              Add folder
            </div>
          </UiButton>
          <ExplorerSelectionBar :selected-items="selectedItems" />
          <Float.Base
            v-if="showOptionsMenu"
            class="hidden tablet-h:block pointer-events-auto!"
          >
            <Float.Trigger>
              <UiButton icon="ThreeDots" />
            </Float.Trigger>
            <Float.Component class="z-100">
              <FileContextMenu
                v-if="isSingleFileSelected"
                :file="selectedFiles[0]"
                @clicked="closeFloat"
              />
              <MultipleFilesContextMenu
                v-else-if="isMultipleFilesSelected"
                :files="selectedFiles"
                @clicked="closeFloat"
              />
            </Float.Component>
          </Float.Base>
          <UiButton
            icon="ThreeDots"
            class="tablet-h:hidden"
            @click="handleMobileThreeDotsClick"
          />
        </div>
      </div>

      <div class="tablet-h:mt-4 flex flex-col items-end">
        <div
          class="hidden tablet-h:block font-primary text-[12px] text-gray-600/60 mb-2 text-right whitespace-nowrap"
        >
          Adding copyright as
        </div>

        <Float.Base
          v-if="explorerStore.selectedCopyrightOwner"
          position="bottom"
        >
          <Float.Trigger>
            <UiButton
              class="px-2! tablet-h:px-3! min-w-10! max-w-10 tablet-h:max-w-[184px]"
            >
              <Avatar
                :src="explorerStore.selectedCopyrightOwner.avatarUrl"
                :size="avatarSize"
              />
              <div
                class="hidden tablet-h:block mx-1.5 whitespace-nowrap text-ellipsis overflow-hidden max-w-[108px] font-secondary"
              >
                {{ explorerStore.selectedCopyrightOwner.name }}
              </div>
              <Icon
                :size="20"
                class="hidden tablet-h:block stroke-gray-800 rotate-180"
                name="ChevronUp"
              />
            </UiButton>
          </Float.Trigger>
          <Float.Component>
            <CopyrightOwnersList />
          </Float.Component>
        </Float.Base>

        <Skeleton
          v-else
          class="w-[42px] tablet-h:w-[184px] h-10 tablet-h:h-8 rounded-sm"
        />
      </div>
    </div>

    <!-- Table -->
    <ExplorerTable
      ref="tableRef"
      :items="items"
      @sort="handleSort"
      @scroll="handleScroll"
    />

    <div
      v-if="showCallout"
      class="w-full absolute bottom-4 z-10 px-3 -mx-3 flex justify-center tablet-h:hidden"
    >
      <div
        class="w-full max-w-[404px] rounded-[10px] bg-[#F5F7F9] border border-gray-300/30 px-3 py-2 flex gap-x-3"
      >
        <Pill class="h-fit" success>Pro</Pill>
        <div class="leading-[21px]">
          <div class="font-secondary text-[14px] text-gray-800 inline">
            Add unlimited copyrights plus more features.
          </div>
          <RouterLink to="/upgrade" class="inline-block w-fit">
            <Link>Upgrade to Pro</Link>
          </RouterLink>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Pill, UiButton, Float, Icon, Avatar, Skeleton, Table } from '@dacr/ui'
import Breadcrumb from '@/shared/components/Breadcrumb.vue'
import { usePath } from '@/shared/composables/usePath'
import { graphql } from '@/shared/graphql/router'
import { ItemSort } from '@/shared/graphql/router/graphql'
import {
  UserPermission,
  useCopyrightOwnersNewQuery,
  type CopyrightOwnerNew,
} from '@/shared/graphql/legacy/generated'
import { useQuery } from '@vue/apollo-composable'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { useUploadFlow } from '@/features/upload/composables/useUploadFlow'
import { useRoute, useRouter } from 'vue-router'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import ExplorerTable from '@/features/explorer/components/ExplorerTable.vue'
import ExplorerSelectionBar from '@/features/explorer/components/ExplorerSelectionBar.vue'
import { useExplorerStore } from '@/features/explorer/store'
import FileContextMenu from '@/features/explorer/components/FileContextMenu.vue'
import MultipleFilesContextMenu from '@/features/explorer/components/MultipleFilesContextMenu.vue'
import { useUserStore } from '@/shared/stores/user'
import { useHead } from '@unhead/vue'
import { useDevice } from '@/shared/composables/useDevice'
import { useAvailableHeight } from '@/shared/composables/useAvailableHeight'
import { TableTypes } from '@/shared/types'
import CopyrightOwnersList from '@/features/copyright/components/CopyrightOwnersList.vue'
import { sortExplorerItems } from '@/features/explorer/utils/sortExplorerItems'
import Link from '@/shared/components/Link.vue'
import { useBillingStore } from '@/features/billing/store'
import UpsellBanner from '@/features/billing/components/UpsellBanner.vue'
import type { Item } from '@/shared/graphql/router/graphql'
import { isFile } from '@/features/explorer/utils/itemGuards'

const FOLDER_BY_PATH_QUERY = graphql(`
  query FolderByPath(
    $path: String!
    $sort: ItemSort
    $first: Int
    $after: String
  ) {
    folderByPath(path: $path) {
      id
      name
      path
      createdAt
      totalFiles
      items(input: { sort: $sort, first: $first, after: $after }) {
        edges {
          cursor
          node {
            ... on File {
              id
              name
              type
              description
              role
              permissions
              copyright {
                id
                status
                registrations {
                  certificateUrl
                  origin {
                    id
                    name
                  }
                  timestamp
                }
              }
              metadata {
                originalFilename
                size
              }
              asset {
                previewImageUrl
              }
              settings {
                isPrivate
              }
              owner {
                ... on User {
                  id
                  name
                  avatar {
                    url
                  }
                }
                ... on Organization {
                  id
                  name
                  avatar {
                    url
                  }
                }
              }
              coOwners(input: { first: 3, filterBy: ACCEPTED }) {
                totalCount
                edges {
                  node {
                    ... on EntityWithRole {
                      entity {
                        ... on User {
                          id
                          name
                          avatar {
                            url
                          }
                        }
                        ... on Organization {
                          id
                          name
                          avatar {
                            url
                          }
                        }
                      }
                      role
                    }
                  }
                }
              }
              authors(input: { first: 3, filterBy: ACCEPTED }) {
                totalCount
                edges {
                  node {
                    ... on EntityWithRole {
                      entity {
                        ... on User {
                          id
                          name
                          avatar {
                            url
                          }
                        }
                        ... on Organization {
                          id
                          name
                          avatar {
                            url
                          }
                        }
                      }
                      role
                    }
                  }
                }
              }
              viewers(input: { first: 10 }) {
                totalCount
              }
            }
            ... on Folder {
              id
              name
              path
              createdAt
              totalFiles
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
        totalFiles
      }
    }
  }
`)

const {
  result: copyrightOwnersQuery,
  loading: copyrightOwnersLoading,
  fetchMore: fetchMoreOwners,
} = useCopyrightOwnersNewQuery()

const device = useDevice()
const { availableHeight } = useAvailableHeight()
const route = useRoute()
const path = usePath()
const router = useRouter()
const table = Table.useTable(TableTypes.Explorer)
const { open } = useDialog()

const billingStore = useBillingStore()
const explorerStore = useExplorerStore()
const { showUploadDialog, uploadFiles } = useUploadFlow()
const userStore = useUserStore()

const dragging = ref(false)
const ownersRef = ref<HTMLElement>()
const tableRef = ref<InstanceType<typeof ExplorerTable>>()

const showCallout = computed(
  () =>
    !userStore.user?.permissions.includes(UserPermission.Pro) &&
    explorerStore.currentFolder?.items.filter(isFile).length
)

const copyrightOwners = computed(
  () => copyrightOwnersQuery.value?.copyrightOwnersNew.items
)

const componentStyles = computed(() => ({
  height: `${availableHeight.value}px`,
}))

const avatarSize = computed(() =>
  device.isMobile.value || device.isTabletVertical.value ? 24 : 18
)

const overlayClasses = computed(() => ({
  ...(dragging.value &&
    !loading.value &&
    canUpload.value && {
      'border-2': true,
      'border-dashed': true,
      'border-primary': true,
    }),
}))

const showBreadcrumb = computed(() =>
  device.isMobile.value || device.isTabletVertical.value
    ? path.length > 1
    : true
)

const handleLocationUpdate = (pathArr: string[]) => {
  if (pathArr.length === 1) {
    router.push({ name: 'FileExplorerRootFolderView' })
  } else {
    router.push({
      name: 'FileExplorerFolderView',
      params: { path: pathArr.slice(1) },
    })
  }
}

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  const tableEl = tableRef.value?.$el
  if (tableEl && !tableEl.contains(target)) {
    table.resetSelection()
  }
}

const closeFloat = () => {
  document.body.click()
}

// Get explorer items
const sortBy = ref<ItemSort>(ItemSort.NameAsc)

const handleSort = (sort: readonly [string, 'asc' | 'desc']) => {
  if (sort[0] === 'name' && sort[1] === 'asc') sortBy.value = ItemSort.NameAsc
  if (sort[0] === 'name' && sort[1] === 'desc') sortBy.value = ItemSort.NameDesc
  if (sort[0] === 'timestamp' && sort[1] === 'asc')
    sortBy.value = ItemSort.TimestampAsc
  if (sort[0] === 'timestamp' && sort[1] === 'desc')
    sortBy.value = ItemSort.TimestampDesc

  explorerStore.setSort(sortBy.value)
}

const pathParam = route.params.path as string[] | null
let folderPath = '/'

if (pathParam) {
  folderPath += (pathParam as string[]).join('/')
}

const variables = computed(() => ({
  path: folderPath,
  sort: sortBy.value,
  first: 50,
}))

const { result, loading, fetchMore } = useQuery(
  FOLDER_BY_PATH_QUERY,
  variables,
  {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-only',
    pollInterval: 10_000,
    clientId: 'router',
  }
)

const extractItems = (data: typeof result.value): Item[] => {
  if (!data?.folderByPath?.items?.edges) return []
  return data.folderByPath.items.edges.map((edge) => edge.node as Item)
}

watch(
  result,
  () => {
    if (!result.value) return

    const folderData = result.value.folderByPath
    const folderItems = extractItems(result.value)

    explorerStore.setCurrentFolder({
      id: folderData.id,
      name: folderData.name,
      path: folderData.path,
      createdAt: folderPath === '/' ? null : folderData.createdAt,
      totalFiles: folderData.totalFiles,
      items: folderItems,
      sortBy: sortBy.value,
      endCursor: folderData.items.pageInfo.endCursor,
      hasNextPage: folderData.items.pageInfo.hasNextPage,
    })
  },
  { immediate: true }
)

watch(
  copyrightOwners,
  () => {
    if (!copyrightOwners.value?.length) return

    const primaryOwner = copyrightOwners.value.find((owner) => owner.isSelected)
    if (!primaryOwner) return

    explorerStore.setCopyrightOwner(primaryOwner as CopyrightOwnerNew)
  },
  { immediate: true }
)

const items = computed(() => {
  if (loading.value && !explorerStore.currentFolder?.hasNextPage)
    return undefined

  const folderItems = extractItems(result.value)

  const sorted = sortExplorerItems(folderItems)

  return (sorted || explorerStore.currentFolder?.items) as Item[]
})

const title = ref(`${pathParam?.at(-1) || 'Home'} - Dacr`)
useHead({ title })

const selectedItems = computed(() =>
  (items.value || []).filter((item) => table.isRowSelected(item.id))
)

const selectedFiles = computed(() => selectedItems.value.filter(isFile))

const areAllSelectedRowsFiles = computed(
  () => selectedItems.value.length > 0 && selectedItems.value.every(isFile)
)

const showOptionsMenu = computed(() => areAllSelectedRowsFiles.value)

const isSingleFileSelected = computed(
  () => areAllSelectedRowsFiles.value && table.selectedRows.value.length === 1
)

const isMultipleFilesSelected = computed(
  () => areAllSelectedRowsFiles.value && table.selectedRows.value.length > 1
)

const canUpload = computed(() =>
  userStore.user?.permissions.includes(UserPermission.Upload)
)

// Drag & drop
const handleDragOver = () => {
  if (loading.value || !canUpload.value) return
  dragging.value = true
}

const handleDragLeave = () => {
  dragging.value = false
}

const handleDrop = (event: DragEvent) => {
  if (loading.value || !canUpload.value) return

  dragging.value = false

  const files = event.dataTransfer?.files
  if (!files?.length) return

  uploadFiles(files)
}

const handleUploadClick = () => {
  showUploadDialog()
}

const handleNewFolderClick = () => {
  open(Dialogs.AddFolder, {
    props: { folderId: result.value?.folderByPath?.id },
  })
}

const handleMobileThreeDotsClick = () => {
  open(Dialogs.ThreeDotsMenu, {
    props: { items: selectedItems.value },
  })
}

// Pagination
const fetchNextPage = () => {
  const folder = explorerStore.currentFolder
  if (folder?.hasNextPage && folder.endCursor && !loading.value) {
    fetchMore({
      variables: {
        after: folder.endCursor,
      },
      updateQuery: (previousData, { fetchMoreResult: newData }) => {
        if (!newData) return previousData

        const prevEdges = previousData.folderByPath.items.edges
        const newEdges = newData.folderByPath.items.edges

        if (!newEdges.length) return previousData

        return {
          ...newData,
          folderByPath: {
            ...newData.folderByPath,
            items: {
              ...newData.folderByPath.items,
              edges: [...prevEdges, ...newEdges],
            },
          },
        }
      },
    })
  }
}

const handleScroll = (event: Event) => {
  const element = event.target as HTMLElement
  const elementHeight = element.scrollHeight
  const scrollPos = element.clientHeight + element.scrollTop

  if (scrollPos / elementHeight >= 0.5) {
    fetchNextPage()
  }
}

// Copyright owners pagination
const fetchNextPageOfOwners = () => {
  if (
    copyrightOwnersQuery.value?.copyrightOwnersNew.next &&
    !copyrightOwnersLoading.value
  ) {
    fetchMoreOwners({
      variables: {
        next: copyrightOwnersQuery.value.copyrightOwnersNew.next,
      },
      updateQuery: (previousData, { fetchMoreResult: newData }) => {
        if (!newData) return previousData

        return {
          ...newData,
          copyrightOwnersNew: {
            ...newData.copyrightOwnersNew,
            items: [
              ...previousData.copyrightOwnersNew.items,
              ...newData.copyrightOwnersNew.items,
            ],
          },
        }
      },
    })
  }
}

const handleOwnersScroll = (event: Event) => {
  const element = event.target as HTMLElement
  const elementHeight = element.scrollHeight
  const scrollPos = element.clientHeight + element.scrollTop

  if (scrollPos / elementHeight >= 0.5) {
    fetchNextPageOfOwners()
  }
}

watch(ownersRef, () => {
  if (!ownersRef.value) return
  ownersRef.value.addEventListener('scroll', handleOwnersScroll)
})

onBeforeUnmount(() => {
  ownersRef.value?.removeEventListener('scroll', handleOwnersScroll)
  explorerStore.reset()
  table.resetSelection()
})
</script>
